import { Formik, Form, Field } from 'formik'
import classNames from 'classnames'
import Input from 'components/atoms/Input'
import LoadingIcon from 'components/atoms/LoadingIcon'
import notify from 'components/atoms/Notify'
import Selection from 'components/molecules/Selection'
import { useEffect, useState } from 'react'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiDelete from 'lib/network/apiDelete'
import { useHistory, useLocation } from 'react-router-dom'
import validations from 'validations'
import _, { get } from 'lodash'
import TextArea from 'antd/lib/input/TextArea'
import { useSelector } from 'react-redux'
import Switch from 'components/atoms/Switch'
import ViewCompanyCar from './modals/CompanyCar'
import { Dialog } from '@headlessui/react'
import EmptyState from 'components/pageComponents/EmptyState'
import moment from 'moment'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { arSA, enUS } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { VEHICLE_FEATURE_KEYS, defaultVehicleFeatures } from './vehicleFeatures'

registerLocale('ar', arSA)
registerLocale('en', enUS)

const PRICING_ACTIVE_FIELDS = ['isDailyActive', 'isWeeklyActive', 'isMonthlyActive', 'isYearlyActive']

const LabelContent = ({ label, required }) => (
  <span className="inline-flex items-center gap-1">
    <span>{label}</span>
    {required && <span className="text-red-500">*</span>}
  </span>
)

const FieldLabel = ({ htmlFor, label, required }) => (
  <label htmlFor={htmlFor} className="block text-sm font-normal text-gray-700 mb-1">
    <LabelContent label={label} required={required} />
  </label>
)

const PRICES_NUMBER_WRAP = 'w-full md:max-w-[160px]'
const PRICES_DATE_WRAP = 'w-full md:max-w-[220px]'
const PRICES_LABEL_CLASS = 'block text-sm font-normal text-gray-700 mb-1'
const PRICES_INPUT_CLASS = 'w-full'
const PRICES_DATE_INPUT_CLASS = 'block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md'
const CAR_IMAGE_TILE_CLASS = 'relative h-[135px] w-[240px] border border-dashed border-gray-300 rounded-md overflow-hidden bg-gray-100'
const CAR_IMAGE_IMG_CLASS = 'h-full w-full object-cover rounded'

const countActivePricingTiers = values => PRICING_ACTIVE_FIELDS.filter(field => values[field] !== false).length

const PricesNumberField = ({ id, name, label, required }) => (
  <div className={PRICES_NUMBER_WRAP}>
    <FieldLabel htmlFor={id} label={label} required={required} />
    <Field type="number" name={name} as={Input} id={id} className={PRICES_INPUT_CLASS} />
  </div>
)

const PricesDateField = ({ id, label, selected, onChange, isArabic, datePickerLocale, placeholderText, required }) => (
  <div className={PRICES_DATE_WRAP}>
    <FieldLabel htmlFor={id} label={label} required={required} />
    <div dir={isArabic ? 'rtl' : 'ltr'}>
      <DatePicker
        id={id}
        selected={selected}
        onChange={onChange}
        showTimeSelect
        timeFormat="HH:mm"
        timeIntervals={15}
        timeCaption={isArabic ? 'الوقت' : 'Time'}
        dateFormat="MMM d, yyyy h:mm aa"
        locale={datePickerLocale}
        className={PRICES_DATE_INPUT_CLASS}
        placeholderText={placeholderText}
        isClearable
      />
    </div>
  </div>
)

const PricingRateField = ({ id, name, activeField, label, values, setFieldValue, t, isArabic, required }) => {
  const handleToggle = checked => {
    if (!checked && countActivePricingTiers({ ...values, [activeField]: false }) === 0) {
      notify.error(t('carProfile.messages.atLeastOnePricingTier'))
      return
    }
    setFieldValue(activeField, checked)
  }

  return (
    <div className={classNames('flex items-end gap-2 w-full', isArabic && 'flex-row-reverse')}>
      <div className="shrink-0 pb-1">
        <Switch value={values[activeField] !== false} onChange={handleToggle} />
      </div>
      <div className={classNames(PRICES_NUMBER_WRAP, 'flex-1 min-w-0')}>
        <FieldLabel htmlFor={id} label={label} required={required} />
        <Field type="number" name={name} as={Input} id={id} className={PRICES_INPUT_CLASS} />
      </div>
    </div>
  )
}

// A normal form field (label heading on top, like the Prices tab) with the price input
// and its active toggle side by side. Toggles are fully independent (both may be off) —
// no "at least one active" guard, unlike PricingRateField.
const EXTRA_KEYS = ['childSeat', 'tourGuide', 'tent']
const defaultExtras = Object.fromEntries(EXTRA_KEYS.map(key => [key, { active: false, pricePerDay: 0 }]))

const InsuranceRateField = ({ id, name, activeField, label, suffix, values, setFieldValue, isArabic }) => (
  <div className="w-full md:max-w-[280px]">
    <label htmlFor={id} className={PRICES_LABEL_CLASS}>
      {label}
      {suffix ? ` (${suffix})` : ''}
    </label>
    <div className={classNames('flex items-center gap-3', isArabic && 'flex-row-reverse')}>
      <Field type="number" name={name} as={Input} id={id} className={classNames(PRICES_NUMBER_WRAP, PRICES_INPUT_CLASS)} min={0} />
      <Switch value={_.get(values, activeField) === true} onChange={checked => setFieldValue(activeField, checked)} />
    </div>
  </div>
)

const isCarFieldEmpty = value => value === '' || value === null || value === undefined

const collectMissingCarRequiredNotes = (values, carImages, t, { isCompanyRole, hasAdminAccess, isSuperCompanyUser }) => {
  const notes = []
  if (!values) return notes

  if (isCarFieldEmpty(values.modelYear)) notes.push(t('carProfile.needActionNotes.missingModelYear'))
  if (isCarFieldEmpty(values.car?.make)) notes.push(t('carProfile.needActionNotes.missingMake'))
  if (isCarFieldEmpty(values.car?.model)) notes.push(t('carProfile.needActionNotes.missingModel'))
  if (isCarFieldEmpty(values.car?.numberOfSeats)) notes.push(t('carProfile.needActionNotes.missingNumberOfSeats'))
  if (isCarFieldEmpty(values.car?.numberOfDoors)) notes.push(t('carProfile.needActionNotes.missingNumberOfDoors'))
  if (isCarFieldEmpty(values.car?.driveWheel)) notes.push(t('carProfile.needActionNotes.missingDriveWheel'))
  if (isCarFieldEmpty(values.car?.shifter)) notes.push(t('carProfile.needActionNotes.missingShifter'))
  if (isCarFieldEmpty(values.color)) notes.push(t('carProfile.needActionNotes.missingColor'))
  if (isCarFieldEmpty(values.insurance)) notes.push(t('carProfile.needActionNotes.missingInsurance'))
  if (isCarFieldEmpty(values.insuranceFee)) notes.push(t('carProfile.needActionNotes.missingInsuranceFee'))
  if (isCarFieldEmpty(values.fuelPolicy)) notes.push(t('carProfile.needActionNotes.missingFuelPolicy'))
  if (isCarFieldEmpty(values.status)) notes.push(t('carProfile.needActionNotes.missingStatus'))
  if (isCarFieldEmpty(values.plateCategory)) notes.push(t('carProfile.needActionNotes.missingPlateCategory'))

  const showCompanyField = !isCompanyRole || hasAdminAccess
  if (showCompanyField && isCarFieldEmpty(values.company)) notes.push(t('carProfile.needActionNotes.missingCompany'))

  const showBranchField = isSuperCompanyUser || !isCompanyRole || hasAdminAccess
  if (showBranchField && isCarFieldEmpty(values.branch)) notes.push(t('carProfile.needActionNotes.missingBranch'))

  const reg = values.registrationNumber
  // eslint-disable-next-line
  if (isCarFieldEmpty(reg) || (typeof reg === 'string' && reg.trim() === '')) {
    notes.push(t('carProfile.needActionNotes.missingRegistrationNumber'))
  }

  if (isCarFieldEmpty(values.dailyRate)) notes.push(t('carProfile.needActionNotes.missingDailyRate'))
  if (isCarFieldEmpty(values.weeklyRate)) notes.push(t('carProfile.needActionNotes.missingWeeklyRate'))
  if (isCarFieldEmpty(values.monthlyRate)) notes.push(t('carProfile.needActionNotes.missingMonthlyRate'))
  if (isCarFieldEmpty(values.yearlyRate)) notes.push(t('carProfile.needActionNotes.missingYearlyRate'))
  if (isCarFieldEmpty(values.extraKMPerDay)) notes.push(t('carProfile.needActionNotes.missingExtraKMPerDay'))
  if (isCarFieldEmpty(values.extraKMPerWeek)) notes.push(t('carProfile.needActionNotes.missingExtraKMPerWeek'))
  if (isCarFieldEmpty(values.extraKMPerMonth)) notes.push(t('carProfile.needActionNotes.missingExtraKMPerMonth'))
  if (isCarFieldEmpty(values.extraKMPerYear)) notes.push(t('carProfile.needActionNotes.missingExtraKMPerYear'))
  if (isCarFieldEmpty(values.depositAmount)) notes.push(t('carProfile.needActionNotes.missingDepositAmount'))

  if (values?.status === 'out of service' && isCarFieldEmpty(values.outOfServiceDays)) {
    notes.push(t('carProfile.needActionNotes.missingOutOfServiceDays'))
  }

  const pendingCompanyImages = (carImages || []).filter(image => image.status === 'pending' || image.status === 'rejected')
  if (pendingCompanyImages.length > 0) {
    notes.push(t('carProfile.needActionNotes.pendingCompanyImages', { count: pendingCompanyImages.length }))
  }

  return notes
}

const CarProfile = ({ readOnly, refetch, loading, carData, carImages, isEdit, id, setCarImages }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Dropdown option lists load asynchronously. Submitting before they arrive makes the
  // model -> market segment lookup below fail on a car that actually has a segment, so the
  // submit button stays disabled until every list the form needs has resolved once.
  const [loadedRefs, setLoadedRefs] = useState({})
  const markRefLoaded = key => setLoadedRefs(prev => (prev[key] ? prev : { ...prev, [key]: true }))
  const [companyNames, setCompanyNames] = useState([])
  const [companyData, setCompanyData] = useState({})
  // const [params, setParams] = useState({ t: tabs[0].id })
  const [params, setParams] = useState({ t: 1 })
  const [branches, setBranches] = useState([])

  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [colors, setColors] = useState([])
  const [insurances, setInsurances] = useState([])
  const [fuelPolicies, setFuelPolicies] = useState([])
  const [driveWheels, setDriveWheels] = useState([])
  const [marketSegments, setMarketSegments] = useState([])
  const [refundPolicies, setRefundPolicies] = useState([])

  const [selectedMakes, setSelectedMakes] = useState([])
  const [selectedModels, setSelectedModels] = useState([])
  const [selectedColors, setSelectedColors] = useState([])
  const [selectedInsurances, setSelectedInsurances] = useState([])
  const [selectedFuelPolicies, setSelectedFuelPolicies] = useState([])
  const [selectedDriveWheels, setSelectedDriveWheels] = useState([])
  const [selectedMarketSegments, setSelectedMarketSegments] = useState([])
  const [selectedRefundPolicies, setSelectedRefundPolicies] = useState([])

  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const datePickerLocale = isArabic ? 'ar' : 'en'

  const parseDateSafe = value => {
    if (!value) return null
    // Parse using English locale since backend stores dates in English format
    const parsedMoment = moment(value).locale('en')
    if (!parsedMoment.isValid()) return null
    return parsedMoment.toDate()
  }

  const getLocalizedLabel = (enValue, arValue) => {
    if (isArabic) {
      return arValue || enValue || ''
    }
    return enValue || arValue || ''
  }

  const createOption = (id, enValue, arValue, extra = {}) => {
    const enName = enValue || ''
    const arName = arValue || ''
    return {
      id,
      enName,
      arName,
      name: getLocalizedLabel(enName, arName),
      ...extra,
    }
  }

  const cloneOption = option => {
    if (!option) return option
    const { id, enName, arName, name, ...rest } = option
    const baseEn = enName ?? name
    const baseAr = arName ?? name
    return createOption(id, baseEn, baseAr, rest)
  }

  const relocalizeOptions = options =>
    options?.map(option => {
      if (!option) return option
      const { enName, arName, name, ...rest } = option
      const baseEn = enName ?? name
      const baseAr = arName ?? name
      return {
        ...rest,
        id: option.id,
        enName: baseEn,
        arName: baseAr,
        name: getLocalizedLabel(baseEn, baseAr),
      }
    }) || []

  // Ordered for display; ids are stable because tab 5 drives the submit-bar
  // visibility below and appear in bookmarked `?t=` URLs.
  const tabs = [
    { id: 1, name: t('carProfile.tabs.rentalCarDetail'), href: '#', current: false },
    { id: 2, name: t('carProfile.tabs.carDetails'), href: '#', current: false },
    { id: 6, name: t('carProfile.tabs.vehicleFeatures'), href: '#', current: false },
    { id: 3, name: t('carProfile.tabs.prices'), href: '#', current: false },
    { id: 7, name: t('carProfile.tabs.insurancePrices'), href: '#', current: false },
    { id: 8, name: t('carProfile.tabs.extras'), href: '#', current: false },
    { id: 5, name: t('carProfile.tabs.companyRequestedImages'), href: '#', current: false },
  ]
  const selectDateTimePlaceholder = t('common.placeholders.selectDateTime')

  const history = useHistory()
  const location = useLocation()
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  const isSuperCompanyUser = userFromStore?.superCompanyUser

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)

  // Legacy Car Images tab (?t=4) was removed — fall back to rental car detail
  useEffect(() => {
    if (params.t === 4) {
      setParams({ t: 1 })
    }
  }, [params.t])

  useEffect(() => {
    if (isEdit && carData) {
      if (carData.car?.make) {
        const makeValue = carData.car.make
        const selectedMake = _.isObject(makeValue)
          ? createOption(makeValue._id || makeValue.id, makeValue.make, makeValue.makeAr)
          : createOption(makeValue, makeValue, carData.car?.makeAr)
        if (selectedMake.id) {
          setSelectedMakes([selectedMake])
        }
      }
      if (carData.car?.model) {
        const modelValue = carData.car.model
        const selectedModel = _.isObject(modelValue)
          ? createOption(modelValue._id || modelValue.id, modelValue.modal, modelValue.modalAr)
          : createOption(modelValue, modelValue, carData.car?.modelAr)
        if (selectedModel.id) {
          setSelectedModels([selectedModel])
        }
      }
      if (carData.color) {
        const colorValue = carData.color
        const selectedColor = _.isObject(colorValue)
          ? createOption(colorValue._id || colorValue.id, colorValue.color, colorValue.colorAr)
          : createOption(colorValue, colorValue, carData.colorAr)
        if (selectedColor.id) {
          setSelectedColors([selectedColor])
        }
      }
      if (carData.fuelPolicy) {
        const selectedFuelPolicy = createOption(carData.fuelPolicy, carData.fuelPolicy, carData.fuelPolicyAr)
        if (selectedFuelPolicy.id) {
          setSelectedFuelPolicies([selectedFuelPolicy])
        }
      }
      if (carData.car?.driveWheel) {
        const selectedDriveWheel = createOption(carData.car.driveWheel, carData.car.driveWheel, carData.car?.driveWheelAr)
        if (selectedDriveWheel.id) {
          setSelectedDriveWheels([selectedDriveWheel])
        }
      }
      if (carData.car?.marketSegment) {
        const selectedMarketSegment = createOption(carData.car.marketSegment, carData.car.marketSegment, carData.car?.marketSegmentAr)
        if (selectedMarketSegment.id) {
          setSelectedMarketSegments([selectedMarketSegment])
        }
      }
      if (carData.insurance) {
        const selectedInsurance = createOption(carData.insurance, carData.insurance, carData.insuranceAr)
        if (selectedInsurance.id) {
          setSelectedInsurances([selectedInsurance])
        }
      }
      if (carData.refundPolicies && _.isArray(carData.refundPolicies)) {
        const selectedRefundPoliciesList = carData.refundPolicies.map(policy =>
          createOption(policy._id || policy, policy.groupName || policy.name || policy, policy.groupNameAr || policy.nameAr)
        )
        setSelectedRefundPolicies(selectedRefundPoliciesList)
      }
      if (carData.company) {
        fetchBranches(carData.company)
      }
    }
  }, [carData, isEdit, isArabic])

  useEffect(() => {
    if (hasAdminAccess) {
      fetchCompanies()
    }
    fetchMakes()
    fetchModels()
    fetchColors()
  }, [hasAdminAccess, i18n.language])

  useEffect(() => {
    setMakes(prev => relocalizeOptions(prev))
    setModels(prev => relocalizeOptions(prev))
    setColors(prev => relocalizeOptions(prev))
    setInsurances(prev => relocalizeOptions(prev))
    setFuelPolicies(prev => relocalizeOptions(prev))
    setDriveWheels(prev => relocalizeOptions(prev))
    setMarketSegments(prev => relocalizeOptions(prev))
    setRefundPolicies(prev => relocalizeOptions(prev))
    setCompanyNames(prev => relocalizeOptions(prev))
    setBranches(prev => relocalizeOptions(prev))
    setSelectedMakes(prev => relocalizeOptions(prev))
    setSelectedModels(prev => relocalizeOptions(prev))
    setSelectedColors(prev => relocalizeOptions(prev))
    setSelectedInsurances(prev => relocalizeOptions(prev))
    setSelectedFuelPolicies(prev => relocalizeOptions(prev))
    setSelectedDriveWheels(prev => relocalizeOptions(prev))
    setSelectedMarketSegments(prev => relocalizeOptions(prev))
    setSelectedRefundPolicies(prev => relocalizeOptions(prev))
  }, [i18n.language])

  const fetchMakes = async (searchQuery = '') => {
    try {
      const response = await apiGet('makes', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const makesList = response.data.makes.map(make => createOption(make._id, make.make, make.makeAr))

        let combinedMakes = [...makesList]

        if (carData?.car?.make && _.isObject(carData.car.make)) {
          const existingMake = createOption(carData.car.make._id, carData.car.make.make, carData.car.make.makeAr)
          if (existingMake.id && !_.find(combinedMakes, { id: existingMake.id })) {
            combinedMakes = [existingMake, ...combinedMakes]
          }
        }

        _.forEach(selectedMakes, selectedMake => {
          if (!_.find(combinedMakes, { id: selectedMake.id })) {
            const localizedSelected = _.find(combinedMakes, ['id', selectedMake.id]) || selectedMake
            combinedMakes.push(localizedSelected)
          }
        })

        setMakes(combinedMakes)
        setSelectedMakes(prevSelected => {
          if (!prevSelected.length) return prevSelected
          return prevSelected.map(option => _.find(combinedMakes, ['id', option.id]) || option)
        })
      } else {
        setMakes(selectedMakes)
      }
    } catch (error) {
      notify.error('Failed to fetch makes')
      setMakes(selectedMakes)
    } finally {
      markRefLoaded('makes')
    }
  }

  const fetchModels = async (searchQuery = '') => {
    try {
      const response = await apiGet('modals', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const modelsList = response.data.modals.map(model => {
          const segment = model.marketSegment
          const hasLinkedSegment = _.isObject(segment)
          return {
            ...createOption(model._id, model.modal, model.modalAr),
            marketSegment: hasLinkedSegment ? segment.marketSegment || '' : '',
            marketSegmentAr: hasLinkedSegment ? segment.marketSegmentAr || '' : '',
          }
        })

        let combinedModels = [...modelsList]

        // eslint-disable-next-line
        if (carData?.car?.model && typeof carData.car.model === 'object') {
          const existingModel = {
            ...createOption(carData.car.model._id, carData.car.model.modal, carData.car.model.modalAr),
            marketSegment: carData.car.model.marketSegment?.marketSegment || carData.car?.marketSegment || '',
            marketSegmentAr: carData.car.model.marketSegment?.marketSegmentAr || carData.car?.marketSegmentAr || '',
          }
          if (!_.find(combinedModels, { id: existingModel.id })) {
            combinedModels = [existingModel, ...combinedModels]
          }
        }

        _.forEach(selectedModels, selectedModel => {
          if (!_.find(combinedModels, { id: selectedModel.id })) {
            combinedModels.push(selectedModel)
          }
        })

        setModels(combinedModels)
        setSelectedModels(prevSelected => {
          if (!prevSelected.length) return prevSelected
          return prevSelected.map(option => _.find(combinedModels, ['id', option.id]) || option)
        })
      } else {
        setModels(selectedModels)
      }
    } catch (error) {
      notify.error('Failed to fetch models')
      setModels(selectedModels)
    } finally {
      markRefLoaded('models')
    }
  }
  const fetchColors = async (searchQuery = '') => {
    try {
      const response = await apiGet('colors', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const colorsList = response.data.colors.map(color => createOption(color._id, color.color, color.colorAr))

        let combinedColors = [...colorsList]
        // eslint-disable-next-line
        if (carData?.color && typeof carData.color === 'object') {
          const existingColor = createOption(carData.color._id, carData.color.color, carData.color.colorAr)
          if (!_.find(combinedColors, { id: existingColor.id })) {
            combinedColors = [existingColor, ...combinedColors]
          }
        }

        _.forEach(selectedColors, selectColor => {
          if (!_.find(combinedColors, { id: selectColor.id })) {
            combinedColors.push(selectColor)
          }
        })

        setColors(combinedColors)
        setSelectedColors(prevSelected => {
          if (!prevSelected.length) return prevSelected
          return prevSelected.map(option => _.find(combinedColors, ['id', option.id]) || option)
        })
      } else {
        setColors(selectedColors)
      }
    } catch (error) {
      notify.error('Failed to fetch colors')
      setColors(selectedColors)
    } finally {
      markRefLoaded('colors')
    }
  }

  const fetchCompanies = async () => {
    try {
      const response = await apiGet('companies', { pageSize: 1000 })
      if (response?.data) {
        const companies = response.data.company.map(company => createOption(company._id, company.name, company.nameAr))
        setCompanyNames(companies)
      } else {
        setCompanyNames([])
      }
    } finally {
      markRefLoaded('companies')
    }
  }
  const fetchBranches = async companyId => {
    try {
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.data) {
        const branchList = response.data.map(branch => createOption(branch._id, branch.name, branch.nameAr))
        setBranches(branchList)
      } else {
        setBranches([])
      }
    } catch (error) {
      notify.error('Failed to fetch branches')
      setBranches([])
    } finally {
      markRefLoaded('branches')
    }
  }
  const fetchInsurances = async (searchQuery = '') => {
    try {
      const response = await apiGet('insurances', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const insuranceList = response.data.insurance.map(item => createOption(item.insurance, item.insurance, item.insuranceAr))

        let combinedInsurances = [...insuranceList]

        // eslint-disable-next-line
        if (carData?.insurance && typeof carData.insurance === 'string') {
          const existingInsurance = createOption(carData.insurance, carData.insurance, carData.insuranceAr)
          if (!_.find(combinedInsurances, { id: existingInsurance.id })) {
            combinedInsurances = [existingInsurance, ...combinedInsurances]
          }
        }

        _.forEach(selectedInsurances, selectedInsurance => {
          if (!_.find(combinedInsurances, { id: selectedInsurance.id })) {
            combinedInsurances.push(selectedInsurance)
          }
        })

        setInsurances(combinedInsurances)
        setSelectedInsurances(prevSelected => {
          if (!prevSelected.length) return prevSelected
          return prevSelected.map(option => _.find(combinedInsurances, ['id', option.id]) || option)
        })
      } else {
        setInsurances(selectedInsurances)
      }
    } catch (error) {
      notify.error('Failed to fetch insurances')
      setInsurances(selectedInsurances)
    } finally {
      markRefLoaded('insurances')
    }
  }
  const fetchFuelPolicy = async (searchQuery = '') => {
    try {
      const response = await apiGet('fuel-policies', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const fuelPolicyList = response.data.fuelPolicy.map(item => createOption(item.fuelPolicy, item.fuelPolicy, item.fuelPolicyAr))

        let combinedFuelPolicies = [...fuelPolicyList]

        // eslint-disable-next-line
        if (carData?.fuelPolicy && typeof carData.fuelPolicy === 'string') {
          const existingFuelPolicy = createOption(carData.fuelPolicy, carData.fuelPolicy, carData.fuelPolicyAr)
          if (!_.find(combinedFuelPolicies, { id: existingFuelPolicy.id })) {
            combinedFuelPolicies = [existingFuelPolicy, ...combinedFuelPolicies]
          }
        }

        _.forEach(selectedFuelPolicies, selectedFuelPolicy => {
          if (!_.find(combinedFuelPolicies, { id: selectedFuelPolicy.id })) {
            combinedFuelPolicies.push(selectedFuelPolicy)
          }
        })

        setFuelPolicies(combinedFuelPolicies)
        setSelectedFuelPolicies(prevSelected => {
          if (!prevSelected.length) return prevSelected
          return prevSelected.map(option => _.find(combinedFuelPolicies, ['id', option.id]) || option)
        })
      } else {
        setFuelPolicies(selectedFuelPolicies)
      }
    } catch (error) {
      notify.error('Failed to fetch fuel policies')
      setFuelPolicies(selectedFuelPolicies)
    } finally {
      markRefLoaded('fuelPolicies')
    }
  }
  const fetchDriveWheels = async (searchQuery = '') => {
    try {
      const response = await apiGet('drive-wheels', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const driveWheelList = response.data.driveWheel.map(item => createOption(item.driveWheel, item.driveWheel, item.driveWheelAr))

        let combinedDriveWheels = [...driveWheelList]

        // eslint-disable-next-line
        if (carData?.car?.driveWheel && typeof carData.car.driveWheel === 'string') {
          const existingDriveWheel = createOption(carData.car.driveWheel, carData.car.driveWheel, carData.car?.driveWheelAr)
          if (!_.find(combinedDriveWheels, { id: existingDriveWheel.id })) {
            combinedDriveWheels = [existingDriveWheel, ...combinedDriveWheels]
          }
        }

        _.forEach(selectedDriveWheels, selectedDriveWheel => {
          if (!_.find(combinedDriveWheels, { id: selectedDriveWheel.id })) {
            combinedDriveWheels.push(selectedDriveWheel)
          }
        })

        setDriveWheels(combinedDriveWheels)
        setSelectedDriveWheels(prevSelected => {
          if (!prevSelected.length) return prevSelected
          return prevSelected.map(option => _.find(combinedDriveWheels, ['id', option.id]) || option)
        })
      } else {
        setDriveWheels(selectedDriveWheels)
      }
    } catch (error) {
      notify.error('Failed to fetch drive wheels')
      setDriveWheels(selectedDriveWheels)
    } finally {
      markRefLoaded('driveWheels')
    }
  }
  const fetchMarketSegment = async (searchQuery = '') => {
    try {
      const response = await apiGet('market-segments', { search: searchQuery, pageSize: 500, pageNumber: 1 })
      if (response?.data) {
        const marketSegmentList = response.data.marketSegment.map(item => createOption(item.marketSegment, item.marketSegment, item.marketSegmentAr))

        let combinedMarketSegments = [...marketSegmentList]

        // eslint-disable-next-line
        if (carData?.car?.marketSegment && typeof carData.car.marketSegment === 'string') {
          const existingMarketSegment = createOption(carData.car.marketSegment, carData.car.marketSegment, carData.car?.marketSegmentAr)
          if (!_.find(combinedMarketSegments, { id: existingMarketSegment.id })) {
            combinedMarketSegments = [existingMarketSegment, ...combinedMarketSegments]
          }
        }

        _.forEach(selectedMarketSegments, selectedMarketSegment => {
          if (!_.find(combinedMarketSegments, { id: selectedMarketSegment.id })) {
            combinedMarketSegments.push(selectedMarketSegment)
          }
        })

        setMarketSegments(combinedMarketSegments)
        setSelectedMarketSegments(prevSelected => {
          if (!prevSelected.length) return prevSelected
          return prevSelected.map(option => _.find(combinedMarketSegments, ['id', option.id]) || option)
        })
      } else {
        setMarketSegments(selectedMarketSegments)
      }
    } catch (error) {
      notify.error('Failed to fetch market segment')
      setMarketSegments(selectedMarketSegments)
    } finally {
      markRefLoaded('marketSegments')
    }
  }

  const fetchRefundPolicies = async (searchQuery = '') => {
    try {
      const response = await apiGet('refund-policies', { query: searchQuery || undefined, limit: 1000, page: 1 })
      if (response?.data) {
        const refundPoliciesList = response.data.refundPolicies.map(policy => createOption(policy._id, policy.groupName || policy.name, policy.groupNameAr || policy.nameAr))

        const combinedRefundPolicies = [...refundPoliciesList]
        // eslint-disable-next-line
        if (carData?.refundPolicies && Array.isArray(carData.refundPolicies)) {
          _.forEach(carData.refundPolicies, existingPolicy => {
            const existingRefundPolicy = {
              id: existingPolicy._id || existingPolicy,
              name: getLocalizedLabel(existingPolicy.groupName || existingPolicy.name || existingPolicy, existingPolicy.groupNameAr || existingPolicy.nameAr),
            }
            if (!_.find(combinedRefundPolicies, { id: existingRefundPolicy.id })) {
              combinedRefundPolicies.push(existingRefundPolicy)
            }
          })
        }

        _.forEach(selectedRefundPolicies, selectedRefundPolicy => {
          if (!_.find(combinedRefundPolicies, { id: selectedRefundPolicy.id })) {
            combinedRefundPolicies.push(selectedRefundPolicy)
          }
        })

        setRefundPolicies(combinedRefundPolicies)
        setSelectedRefundPolicies(prevSelected => {
          if (!prevSelected.length) return prevSelected
          return prevSelected.map(option => _.find(combinedRefundPolicies, ['id', option.id]) || option)
        })
      } else {
        setRefundPolicies(selectedRefundPolicies)
      }
    } catch (error) {
      notify.error('Failed to fetch refund policies')
      setRefundPolicies(selectedRefundPolicies)
    } finally {
      markRefLoaded('refundPolicies')
    }
  }

  useEffect(() => {
    fetchInsurances()
    fetchFuelPolicy()
    fetchDriveWheels()
    fetchMarketSegment()
    if (hasAdminAccess) {
      fetchRefundPolicies()
    }
  }, [hasAdminAccess, i18n.language])

  const setCurrentTab = currentTab => setParams({ t: parseInt(currentTab, 10) })

  const formatDateTimeLocal = isoString => {
    if (!isoString) return ''
    // Parse and format using native Date methods to ensure English numerals
    const d = new Date(isoString)
    const year = d.getFullYear()
    const month = _.padStart(String(d.getMonth() + 1), 2, '0')
    const day = _.padStart(String(d.getDate()), 2, '0')
    const hours = _.padStart(String(d.getHours()), 2, '0')
    const minutes = _.padStart(String(d.getMinutes()), 2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  const formatDateForBackend = date => {
    if (!date) return ''
    // Always format using English numerals by using native Date methods
    // This ensures backend receives standard digits regardless of UI language
    const d = new Date(date)
    const year = d.getFullYear()
    const month = _.padStart(String(d.getMonth() + 1), 2, '0')
    const day = _.padStart(String(d.getDate()), 2, '0')
    const hours = _.padStart(String(d.getHours()), 2, '0')
    const minutes = _.padStart(String(d.getMinutes()), 2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  const getInitialValues = () => {
    if (isEdit && carData) {
      const normalizePlateCategory = value => {
        if (!value) return value
        // eslint-disable-next-line
        const v = typeof value === 'string' ? value.trim() : value
        if (v === 'Private') return 'private'
        if (v === 'Commercial') return 'commercial'
        return v
      }
      return {
        ...carData,
        dailyDiscountStartDate: formatDateTimeLocal(carData.dailyDiscountStartDate),
        dailyDiscountEndDate: formatDateTimeLocal(carData.dailyDiscountEndDate),
        weeklyDiscountStartDate: formatDateTimeLocal(carData.weeklyDiscountStartDate),
        weeklyDiscountEndDate: formatDateTimeLocal(carData.weeklyDiscountEndDate),
        monthlyDiscountStartDate: formatDateTimeLocal(carData.monthlyDiscountStartDate),
        monthlyDiscountEndDate: formatDateTimeLocal(carData.monthlyDiscountEndDate),
        yearlyDiscountStartDate: formatDateTimeLocal(carData.yearlyDiscountStartDate),
        yearlyDiscountEndDate: formatDateTimeLocal(carData.yearlyDiscountEndDate),
        color: carData.color?._id || carData.color || '',
        plateCategory: normalizePlateCategory(carData.plateCategory),
        car: {
          ...carData.car,
          make: carData.car?.make?._id || carData.car?.make || '',
          model: carData.car?.model?._id || carData.car?.model || '',
        },
        refundPolicies: carData.refundPolicies?.map(policy => policy._id || policy) || [],
        // Spread over the defaults so cars saved before vehicle features
        // existed still get all 20 keys.
        features: { ...defaultVehicleFeatures, ...(carData.features || {}) },
        extras: _.merge({}, defaultExtras, carData.extras),
      }
    }

    return {
      modelYear: '',
      insurance: '',
      fuelPolicy: '',
      description: '',
      status: '',
      plateCategory: '',
      color: '',
      dailyRate: '',
      dailyDiscountRate: '',
      allowedKMPerDay: '',
      dailyDiscountStartDate: '',
      dailyDiscountEndDate: '',
      yearlyRate: '',
      yearlyDiscountRate: '',
      allowedKMPerYear: '',
      yearlyDiscountStartDate: '',
      yearlyDiscountEndDate: '',
      weeklyRate: '',
      weeklyDiscountRate: '',
      allowedKMPerWeek: '',
      weeklyDiscountStartDate: '',
      weeklyDiscountEndDate: '',
      monthlyRate: '',
      monthlyDiscountRate: '',
      allowedKMPerMonth: '',
      monthlyDiscountStartDate: '',
      monthlyDiscountEndDate: '',
      depositAmount: '',
      company: '',
      branch: '',
      registrationNumber: '',
      extraKMPerDay: '',
      extraKMPerWeek: '',
      extraKMPerMonth: '',
      extraKMPerYear: '',
      isDailyActive: true,
      isWeeklyActive: true,
      isMonthlyActive: true,
      isYearlyActive: true,

      isStandardInsuranceActive: false,
      fullInsurancePricePerDay: 0,
      isFullInsuranceActive: false,

      // vin: '',
      // Standard Insurance Price is stored in insuranceFee (reused).
      insuranceFee: '',
      car: {
        model: '',
        make: '',
        marketSegment: '',
        shifter: '',
        trim: '',
        numberOfDoors: '',
        numberOfSeats: '',
        engineSize: '',
        generation: '',
        series: '',
        introduced: '',
        ended: '',
        bodyType: '',
        driveWheel: '',
        fuel: '',
      },
      refundPolicies: [],
      refundType: 'standard',
      features: { ...defaultVehicleFeatures },
      extras: _.cloneDeep(defaultExtras),
    }
  }

  // Option lists the form needs before a submit can be trusted; the conditional ones mirror
  // the fetch conditions in the effects above.
  const requiredRefKeys = ['makes', 'models', 'colors', 'insurances', 'fuelPolicies', 'driveWheels', 'marketSegments']
  if (hasAdminAccess) {
    requiredRefKeys.push('companies', 'refundPolicies')
  }
  if (carData?.company) {
    requiredRefKeys.push('branches')
  }
  const isReferenceDataLoading = _.some(requiredRefKeys, key => !loadedRefs[key])

  const handleSubmit = async (values, { setSubmitting }) => {
    if (isReferenceDataLoading) {
      notify.error(t('carProfile.messages.waitForDataToLoad'))
      setSubmitting(false)
      return
    }

    if (countActivePricingTiers(values) === 0) {
      notify.error(t('carProfile.messages.atLeastOnePricingTier'))
      setSubmitting(false)
      return
    }

    const selectedModelOption = _.find(models, { id: values.car?.model })
    if (!selectedModelOption?.marketSegment) {
      notify.error(t('carAuth.messages.modelMissingMarketSegment'))
      setSubmitting(false)
      return
    }

    setIsSubmitting(true)

    // Insurance prices are optional numerics — an empty field must reach the API as 0.
    const coerceInsurancePrice = value => {
      const num = Number(value)
      return Number.isFinite(num) && num >= 0 ? num : 0
    }
    // Standard Insurance Price is stored in insuranceFee (reused).
    values.insuranceFee = coerceInsurancePrice(values.insuranceFee)
    values.fullInsurancePricePerDay = coerceInsurancePrice(values.fullInsurancePricePerDay)
    values.isStandardInsuranceActive = values.isStandardInsuranceActive === true
    values.isFullInsuranceActive = values.isFullInsuranceActive === true
    values.extras = _.mapValues(values.extras, extra => ({
      active: extra?.active === true,
      pricePerDay: coerceInsurancePrice(extra?.pricePerDay),
    }))

    const filteredValues = { ...values }

    const normalizePlateCategory = value => {
      if (!value) return value
      // eslint-disable-next-line
      const v = typeof value === 'string' ? value.trim() : value
      if (v === 'Private') return 'private'
      if (v === 'Commercial') return 'commercial'
      return v
    }
    filteredValues.plateCategory = normalizePlateCategory(filteredValues.plateCategory)

    if (isEdit) {
      delete filteredValues._id
      delete filteredValues.ac
      delete filteredValues.carImages
      delete filteredValues.isRealImages
      delete filteredValues.createdAt
      delete filteredValues.review
      delete filteredValues.statusUpdatedAt
      delete filteredValues.updatedAt
      delete filteredValues.__v
      delete filteredValues.car.createdAt
      delete filteredValues.car.updatedAt
      delete filteredValues.car.ac
      delete filteredValues.car._id
      delete filteredValues.car.__v
      delete filteredValues.index
      delete filteredValues.OfferDriver
      delete filteredValues.car.index
      delete filteredValues.dailyBranchDiscount
      delete filteredValues.monthlyBranchDiscount
      delete filteredValues.weeklyBranchDiscount
      delete filteredValues.yearlyBranchDiscount
    }

    try {
      if (isEdit) {
        const response = await apiPut(`car/edit/${id}`, filteredValues)
        if (response.success) {
          notify.success(t('carProfile.messages.carUpdatedSuccessfully'))
          history.push({ pathname: '/cars', search: location.search })
        } else {
          notify.error(response.message || t('carAuth.messages.modelMissingMarketSegment'))
        }
      } else {
        const response = await apiPost('add/car', values)
        if (response.success) {
          notify.success(t('carProfile.messages.carAddedSuccessfully'))
          history.push({ pathname: '/cars', search: location.search })
        }
      }
    } catch (error) {
      notify.error('An error occurred')
    }
    setIsSubmitting(false)
    setSubmitting(false)
  }

  const deleteCar = () => {
    notify.deleteConfirm({
      title: 'Delete car',
      message: t('carProfile.messages.deleteCarConfirm'),
      onConfirm: async () => {
        const response = await apiDelete(`car/delete/${id}`)
        if (response?.success) {
          history.push({ pathname: '/cars', search: location.search })
          notify.success(t('carProfile.messages.carDeletedSuccessfully'))
        }
      },
    })
  }

  const currentYear = new Date().getFullYear()
  const startYear = 1995

  const years = Array.from({ length: currentYear - startYear + 1 }, (v, k) => {
    const year = startYear + k
    return { id: year, name: year }
  })
  const handleImageClick = (imageUrl, imageId) => {
    setSelectedImage({ url: imageUrl, id: imageId })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedImage(null)
  }

  const visibleTabs = isCompanyRole && !hasAdminAccess ? tabs.filter(tab => tab.id !== 5) : tabs

  return (
    <>
      <Formik initialValues={getInitialValues()} validationSchema={isEdit ? null : validations.AddUserSchema} onSubmit={handleSubmit} enableReinitialize={false}>
        {({ isValid, setFieldValue, values }) => {
          const missingRequiredNotes = collectMissingCarRequiredNotes(values, carImages, t, { isCompanyRole, hasAdminAccess, isSuperCompanyUser })

          return !loading ? (
            <div className="w-full min-w-0 mb-[60px] flex flex-col">
              <div className="bg-white flex w-full min-w-0">
                <Form className="space-y-8 py-6 sm:py-8 w-full min-w-0 mx-auto px-4 sm:px-6 lg:px-8">
                  <div>
                    <div className="border-b border-gray-200 pb-5 sm:pb-0">
                      <h3 className="text-xl font-medium leading-6 text-gray-900">
                        {t('carProfile.title')} {readOnly ? '(Read-only)' : ''}
                      </h3>
                      <div className="mt-3 sm:mt-4">
                        <div className="block">
                          <nav className="-mb-px flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-8">
                            {visibleTabs.map(tab => (
                              <span
                                key={tab.name}
                                onClick={() => setCurrentTab(tab.id)}
                                className={classNames(
                                  tab.id === params?.t
                                    ? 'border-l-indigo-500 sm:border-l-transparent sm:border-b-indigo-500 text-indigo-600'
                                    : 'border-l-transparent sm:border-b-transparent text-gray-500 hover:text-gray-700 hover:border-l-gray-300 sm:hover:border-b-gray-300',
                                  'whitespace-nowrap py-2 px-3 sm:py-4 sm:px-0 border-l-4 sm:border-l-0 sm:border-b-2 font-medium text-sm cursor-pointer rounded-r-md sm:rounded-none'
                                )}
                                aria-current={params?.t === tab.id ? 'page' : undefined}
                              >
                                {tab.name}
                              </span>
                            ))}
                          </nav>
                        </div>
                      </div>
                    </div>

                    <div
                      className={classNames(
                        'mt-4 rounded-lg border px-4 py-3',
                        missingRequiredNotes.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50/80'
                      )}
                      dir={isArabic ? 'rtl' : 'ltr'}
                      role="region"
                      aria-label={t('carProfile.needAction.cardTitle')}
                    >
                      <p className="text-sm font-medium text-gray-900">{t('carProfile.needAction.cardTitle')}</p>
                      {missingRequiredNotes.length > 0 ? (
                        <>
                          <p className="mt-1 text-sm text-amber-900">{t('carProfile.needAction.intro')}</p>
                          <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-amber-900">
                            {missingRequiredNotes.map((note, idx) => (
                              <li key={idx}>{note}</li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <p className="mt-1 text-sm text-emerald-900">{t('carProfile.needAction.allComplete')}</p>
                      )}
                    </div>

                    {params?.t === 1 && (
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <FieldLabel htmlFor="modelYear" label={t('carProfile.form.modelYear')} required />
                            <Selection
                              onChange={value => {
                                if (value && value.id) {
                                  setFieldValue('modelYear', value.id)
                                }
                              }}
                              title={t('carProfile.placeholders.selectModelYear')}
                              list={years}
                              value={values?.modelYear || ''}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <FieldLabel htmlFor="car.model" label={t('carProfile.form.model')} required />
                            <Selection
                              onChange={value => {
                                if (value && value.id) {
                                  setFieldValue('car.model', value.id)
                                  const newSelectedModel = {
                                    ...cloneOption(value),
                                    marketSegment: value.marketSegment || '',
                                    marketSegmentAr: value.marketSegmentAr || '',
                                  }
                                  setSelectedModels([newSelectedModel])
                                  if (!_.find(models, { id: value.id })) {
                                    setModels(prev => [...prev, newSelectedModel])
                                  }
                                  if (value.marketSegment) {
                                    setFieldValue('car.marketSegment', value.marketSegment)
                                    const segmentOption = createOption(value.marketSegment, value.marketSegment, value.marketSegmentAr)
                                    if (!_.find(marketSegments, { id: value.marketSegment })) {
                                      setMarketSegments(prev => [...prev, segmentOption])
                                    }
                                  } else {
                                    setFieldValue('car.marketSegment', '')
                                  }
                                }
                              }}
                              title={t('carProfile.placeholders.selectModel')}
                              list={models}
                              value={values.car?.model || ''}
                              showSearch
                              onSearch={searchQuery => fetchModels(searchQuery)}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <FieldLabel htmlFor="car.make" label={t('carProfile.form.make')} required />
                            <Selection
                              onChange={value => {
                                if (value && value.id) {
                                  setFieldValue('car.make', value.id)
                                  const newSelectedMake = cloneOption(value)
                                  setSelectedMakes([newSelectedMake])
                                  if (!_.find(makes, { id: value.id })) {
                                    setMakes(prev => [...prev, newSelectedMake])
                                  }
                                }
                              }}
                              title={t('carProfile.placeholders.selectMake')}
                              list={makes}
                              value={values.car?.make || ''}
                              showSearch
                              onSearch={searchQuery => fetchMakes(searchQuery)}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <FieldLabel htmlFor="color" label={t('carProfile.form.color')} required />
                            <Selection
                              onChange={value => {
                                if (value && value.id) {
                                  setFieldValue('color', value.id)
                                  const newSelectedColor = cloneOption(value)
                                  setSelectedColors([newSelectedColor])
                                  if (!_.find(colors, { id: value.id })) {
                                    setColors(prev => [...prev, newSelectedColor])
                                  }
                                }
                              }}
                              title={t('carProfile.placeholders.selectColor')}
                              list={colors}
                              value={values?.color || ''}
                              showSearch
                              onSearch={searchQuery => fetchColors(searchQuery)}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <FieldLabel htmlFor="insurance" label={t('carProfile.form.insurance')} required />
                            <Selection
                              onChange={value => {
                                if (value?.id === values?.insurance) {
                                  setFieldValue('insurance', '')
                                } else if (value.id) {
                                  setFieldValue('insurance', value.id)
                                  const newSelectedInsurance = cloneOption(value)
                                  setSelectedInsurances([newSelectedInsurance])
                                  if (!_.find(insurances, { id: value.id })) {
                                    setInsurances(prev => [...prev, newSelectedInsurance])
                                  }
                                }
                              }}
                              title={t('carProfile.placeholders.selectInsurance')}
                              list={insurances}
                              value={values?.insurance || ''}
                              showSearch
                              onSearch={searchQuery => fetchInsurances(searchQuery)}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <label htmlFor="fuelPolicy" className="block text-sm font-normal text-gray-700 mb-1">
                              {t('carProfile.form.fuelPolicy')}
                            </label>
                            <Selection
                              onChange={value => {
                                if (value?.id === values?.fuelPolicy) {
                                  setFieldValue('fuelPolicy', '')
                                } else if (value.id) {
                                  setFieldValue('fuelPolicy', value.id)
                                  const newSelectedFuelPolicy = cloneOption(value)
                                  setSelectedFuelPolicies([newSelectedFuelPolicy])
                                  if (!_.find(fuelPolicies, { id: value.id })) {
                                    setFuelPolicies(prev => [...prev, newSelectedFuelPolicy])
                                  }
                                }
                              }}
                              title={t('carProfile.placeholders.selectFuelPolicy')}
                              list={fuelPolicies}
                              value={values?.fuelPolicy || ''}
                              showSearch
                              onSearch={searchQuery => fetchFuelPolicy(searchQuery)}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <FieldLabel htmlFor="status" label={t('carProfile.form.status')} required />
                            <Selection
                              onChange={value => {
                                if (value && value.id && values.status === value.id) {
                                  setFieldValue('status', '')
                                  if (value.id === 'active' || value.id === 'booked' || value.id === 'inProgress') {
                                    setFieldValue('outOfServiceDays', null)
                                  }
                                } else if (value && value.id) {
                                  setFieldValue('status', value.id)
                                  if (value.id === 'active' || value.id === 'booked' || value.id === 'inProgress') {
                                    setFieldValue('outOfServiceDays', null)
                                  }
                                }
                              }}
                              title={t('carProfile.placeholders.selectStatus')}
                              list={[
                                {
                                  id: 'active',
                                  name: getLocalizedLabel('Active', t('carProfile.statusOptions.active')),
                                },
                                {
                                  id: 'out of service',
                                  name: getLocalizedLabel('Out of Service', t('carProfile.statusOptions.outOfService')),
                                },
                                {
                                  id: 'inProgress',
                                  name: getLocalizedLabel('In Progress', t('carProfile.statusOptions.inProgress')),
                                },
                              ]}
                              value={values?.status || ''}
                              className="w-full"
                            />
                          </div>
                          <div>
                            <FieldLabel htmlFor="numberOfDoors" label={t('carProfile.form.numberOfDoors')} required />
                            <Field type="number" name="car.numberOfDoors" as={Input} id="numberOfDoors" className="w-full" />
                          </div>
                          <div>
                            <FieldLabel htmlFor="numberOfSeats" label={t('carProfile.form.numberOfSeats')} required />
                            <Field type="number" name="car.numberOfSeats" as={Input} id="numberOfSeats" className="w-full" />
                          </div>
                          <div>
                            <FieldLabel htmlFor="driveWheel" label={t('carProfile.form.driveWheel')} required />
                            <Selection
                              onChange={value => {
                                if (value && value.id && values.car.driveWheel === value.id) {
                                  setFieldValue('car.driveWheel', '')
                                } else if (value && value.id) {
                                  setFieldValue('car.driveWheel', value.id)
                                  const newSelectedDriveWheel = cloneOption(value)
                                  setSelectedDriveWheels([newSelectedDriveWheel])
                                  if (!_.find(driveWheels, { id: value.id })) {
                                    setDriveWheels(prev => [...prev, newSelectedDriveWheel])
                                  }
                                }
                              }}
                              title={t('carProfile.placeholders.selectDriveWheel')}
                              list={driveWheels}
                              value={values.car?.driveWheel || ''}
                              showSearch
                              onSearch={searchQuery => fetchDriveWheels(searchQuery)}
                              className="w-full"
                            />
                          </div>

                          <div>
                            <FieldLabel htmlFor="shifter" label={t('carProfile.form.shifter')} required />
                            <Selection
                              onChange={value => {
                                if (value && value.id && values.car?.shifter === value.id) {
                                  setFieldValue('car.shifter', '')
                                } else if (value && value.id) {
                                  setFieldValue('car.shifter', value.id)
                                }
                              }}
                              title={t('carProfile.placeholders.selectShifter')}
                              list={[
                                {
                                  id: 'Manual',
                                  name: getLocalizedLabel('Manual', t('carProfile.shifterOptions.manual')),
                                },
                                {
                                  id: 'Automatic',
                                  name: getLocalizedLabel('Automatic', t('carProfile.shifterOptions.automatic')),
                                },
                              ]}
                              value={values.car?.shifter || ''}
                              className="w-full"
                            />
                          </div>
                          {values?.status === 'out of service' && (
                            <div>
                              <FieldLabel htmlFor="outOfServiceDays" label={t('carProfile.form.outOfServiceDays')} required />
                              <Field type="text" name="outOfServiceDays" as={Input} id="outOfServiceDays" className="w-full" />
                            </div>
                          )}
                          {/* {values?.status === 'booked' && (
                            <>
                              <div>
                                <label htmlFor="rentalStartDate" className="block text-sm font-normal text-gray-700 mb-1">
                                  Booking From
                                </label>
                                <Field type="date" name="rentalStartDate" as={Input} id="rentalStartDate" className="w-full" />
                              </div>
                              <div>
                                <label htmlFor="rentalEndDate" className="block text-sm font-normal text-gray-700 mb-1">
                                  Booking To
                                </label>
                                <Field type="date" name="rentalEndDate" as={Input} id="rentalEndDate" className="w-full" />
                              </div>
                            </>
                          )} */}

                          {(!isCompanyRole || hasAdminAccess) && (
                            <div>
                              <FieldLabel htmlFor="company" label={t('carProfile.form.company')} required />
                              <Selection
                                onChange={value => {
                                  if (value && value.id && values.company === value.id) {
                                    setFieldValue('company', '')
                                  } else if (value && value.id) {
                                    setFieldValue('company', value.id)
                                    fetchBranches(value.id)
                                  }
                                }}
                                title={t('carProfile.placeholders.selectCompanies')}
                                list={companyNames}
                                value={values?.company || ''}
                                className="w-full"
                              />
                            </div>
                          )}
                          {(isSuperCompanyUser || !isCompanyRole || hasAdminAccess) && (
                            <div>
                              <FieldLabel htmlFor="branch" label={t('carProfile.form.branch')} required />
                              <Selection
                                onChange={value => {
                                  if (value?.id === values?.branch) {
                                    setFieldValue('branch', '')
                                  } else if (value?.id) {
                                    setFieldValue('branch', value.id)
                                  }
                                }}
                                title={t('carProfile.placeholders.selectBranch')}
                                list={branches}
                                value={values?.branch || ''}
                                className="w-full"
                              />
                            </div>
                          )}

                          {/* Old per-car refund policies, replaced by Refund Type + the refund engine. Existing
                              values are kept and still apply to bookings paid before go-live.
                          {(!isCompanyRole || hasAdminAccess) && (
                            <div>
                              <FieldLabel htmlFor="refundPolicies" label={t('refundEngine.car.legacyPolicies')} />
                              <Selection
                                onChange={policyIds => setFieldValue('refundPolicies', policyIds || [])}
                                title={t('carProfile.placeholders.selectRefundPolicies')}
                                multiple={true}
                                list={refundPolicies}
                                value={values?.refundPolicies || []}
                                showSearch
                                onSearch={searchQuery => fetchRefundPolicies(searchQuery)}
                                className="w-full"
                              />
                              <p className="mt-1 text-xs text-gray-500">{t('refundEngine.car.legacyHint')}</p>
                            </div>
                          )}
                          */}
                          {(!isCompanyRole || hasAdminAccess) && (
                            <div>
                              <FieldLabel htmlFor="refundType" label={t('refundEngine.car.refundType')} />
                              <Selection
                                onChange={value => value?.id && setFieldValue('refundType', value.id)}
                                list={['standard', 'free_cancellation', 'non_refundable'].map(type => ({ id: type, name: t(`refundEngine.refundType.${type}`) }))}
                                value={values?.refundType || 'standard'}
                                className="w-full"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {params.t === 2 && (
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* <div>
                            <label htmlFor="vin" className="block text-sm font-normal text-gray-700 mb-1">
                              Vin
                            </label>
                            <Field type="text" name="vin" as={Input} id="color" className="w-full" />
                          </div> */}
                          <div>
                            <FieldLabel htmlFor="registrationNumber" label={t('carProfile.form.registrationNumber')} required />
                            <Field type="text" name="registrationNumber" as={Input} id="registrationNumber" className="w-full" />
                          </div>
                          <div>
                            <FieldLabel htmlFor="plateCategory" label={t('carProfile.form.plateCategory')} required />
                            <Selection
                              onChange={value => {
                                if (value && value.id && values?.plateCategory === value.id) {
                                  setFieldValue('plateCategory', '')
                                } else if (value && value.id) {
                                  setFieldValue('plateCategory', value.id)
                                }
                              }}
                              title={t('carProfile.placeholders.selectPlateCategory')}
                              list={[
                                {
                                  id: 'private',
                                  name: getLocalizedLabel(t('carProfile.plateCategoryOptions.private'), t('carProfile.plateCategoryOptions.private')),
                                },
                                {
                                  id: 'commercial',
                                  name: getLocalizedLabel(t('carProfile.plateCategoryOptions.commercial'), t('carProfile.plateCategoryOptions.commercial')),
                                },
                              ]}
                              value={values?.plateCategory || ''}
                              className="w-full"
                            />
                          </div>
                          {/* Delivery is priced from the branch's km ranges (Branch > Delivery tab),
                              so there is no per-car delivery cost to enter here any more. */}
                          <div>
                            <FieldLabel htmlFor="depositAmount" label={t('carProfile.form.depositAmount')} required />
                            <Field type="number" name="depositAmount" as={Input} id="color" className="w-full" />
                          </div>
                          {/* <div>
                            <label htmlFor="generation" className="block text-sm font-normal text-gray-700 mb-1">
                              Generation
                            </label>
                            <Field type="text" name="car.generation" as={Input} id="generation" className="w-full" />
                          </div> */}
                          {/* <div>
                            <label htmlFor="series" className="block text-sm font-normal text-gray-700 mb-1">
                              Series
                            </label>
                            <Field type="text" name="car.series" as={Input} id="series" className="w-full" />
                          </div> */}

                          {/* <div>
                            <label htmlFor="introduced" className="block text-sm font-normal text-gray-700 mb-1">
                              Introduced
                            </label>
                            <Field type="number" name="car.introduced" as={Input} id="introduced" className="w-full" />
                          </div>
                          <div>
                            <label htmlFor="ended" className="block text-sm font-normal text-gray-700 mb-1">
                              Ended
                            </label>
                            <Field type="number" name="car.ended" as={Input} id="ended" className="w-full" />
                          </div> */}
                          {/* <div>
                            <label htmlFor="bodyType" className="block text-sm font-normal text-gray-700 mb-1">
                              Body Type
                            </label>
                            <Field type="text" name="car.bodyType" as={Input} id="bodyType" className="w-full" />
                          </div>
                          <div>
                            <label htmlFor="fuel" className="block text-sm font-normal text-gray-700 mb-1">
                              fuel
                            </label>
                            <Field type="text" name="car.fuel" as={Input} id="fuel" className="w-full" />
                          </div> */}

                          {/* <div>
                            <label htmlFor="trim" className="block text-sm font-normal text-gray-700 mb-1">
                              Trim
                            </label>
                            <Field type="text" name="car.trim" as={Input} id="trim" className="w-full" />
                          </div> */}
                          <div>
                            <FieldLabel htmlFor="engineSize" label={t('carProfile.form.engineSize')} />

                            <Selection
                              onChange={value => {
                                if (value && value.id && values.car?.engineSize === value.id) {
                                  setFieldValue('car.engineSize', '')
                                } else if (value && value.id) {
                                  setFieldValue('car.engineSize', value.id)
                                }
                              }}
                              title={t('carProfile.placeholders.selectEngineSize')}
                              list={[
                                { id: 3, name: '3' },
                                { id: 4, name: '4' },
                                { id: 5, name: '5' },
                                { id: 6, name: '6' },
                                { id: 8, name: '8' },
                              ]}
                              value={values.car?.engineSize || ''}
                              className="w-full"
                            />
                          </div>
                          {/* <div>
                            <label htmlFor="description" className="block text-sm font-normal text-gray-700 mb-1">
                              Description
                            </label>
                            <Field name="description" as={TextArea} type="text" rows="5" className="w-full" />
                          </div> */}
                        </div>
                      </div>
                    )}
                    {params?.t === 3 && (
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6 items-start">
                          <PricingRateField
                            id="dailyRate"
                            name="dailyRate"
                            activeField="isDailyActive"
                            label={t('carProfile.form.dailyRate')}
                            values={values}
                            setFieldValue={setFieldValue}
                            t={t}
                            isArabic={isArabic}
                            required
                          />
                          <PricesNumberField id="dailyDiscountRate" name="dailyDiscountRate" label={t('carProfile.form.dailyDiscountRate')} />
                          <PricesNumberField id="allowedKMPerDay" name="allowedKMPerDay" label={t('carProfile.form.allowedKMPerDay')} required />
                          <PricesNumberField id="extraKMPerDay" name="extraKMPerDay" label={t('carProfile.form.extraKMPerDay')} required />
                          <PricesDateField
                            id="dailyDiscountStartDate"
                            label={t('carProfile.form.dailyDiscountFrom')}
                            selected={parseDateSafe(values.dailyDiscountStartDate)}
                            onChange={date => setFieldValue('dailyDiscountStartDate', formatDateForBackend(date))}
                            isArabic={isArabic}
                            datePickerLocale={datePickerLocale}
                            placeholderText={selectDateTimePlaceholder}
                          />
                          <PricesDateField
                            id="dailyDiscountEndDate"
                            label={t('carProfile.form.dailyDiscountTo')}
                            selected={parseDateSafe(values.dailyDiscountEndDate)}
                            onChange={date => setFieldValue('dailyDiscountEndDate', formatDateForBackend(date))}
                            isArabic={isArabic}
                            datePickerLocale={datePickerLocale}
                            placeholderText={selectDateTimePlaceholder}
                          />
                          <PricingRateField
                            id="weeklyRate"
                            name="weeklyRate"
                            activeField="isWeeklyActive"
                            label={t('carProfile.form.weeklyRate')}
                            values={values}
                            setFieldValue={setFieldValue}
                            t={t}
                            isArabic={isArabic}
                            required
                          />
                          <PricesNumberField id="weeklyDiscountRate" name="weeklyDiscountRate" label={t('carProfile.form.weeklyDiscountRate')} />
                          <PricesNumberField id="allowedKMPerWeek" name="allowedKMPerWeek" label={t('carProfile.form.allowedKMPerWeek')} required />
                          <PricesNumberField id="extraKMPerWeek" name="extraKMPerWeek" label={t('carProfile.form.extraKMPerWeek')} required />
                          <PricesDateField
                            id="weeklyDiscountStartDate"
                            label={t('carProfile.form.weeklyDiscountFrom')}
                            selected={parseDateSafe(values.weeklyDiscountStartDate)}
                            onChange={date => setFieldValue('weeklyDiscountStartDate', formatDateForBackend(date))}
                            isArabic={isArabic}
                            datePickerLocale={datePickerLocale}
                            placeholderText={selectDateTimePlaceholder}
                          />
                          <PricesDateField
                            id="weeklyDiscountEndDate"
                            label={t('carProfile.form.weeklyDiscountTo')}
                            selected={parseDateSafe(values.weeklyDiscountEndDate)}
                            onChange={date => setFieldValue('weeklyDiscountEndDate', formatDateForBackend(date))}
                            isArabic={isArabic}
                            datePickerLocale={datePickerLocale}
                            placeholderText={selectDateTimePlaceholder}
                          />
                          <PricingRateField
                            id="monthlyRate"
                            name="monthlyRate"
                            activeField="isMonthlyActive"
                            label={t('carProfile.form.monthlyRate')}
                            values={values}
                            setFieldValue={setFieldValue}
                            t={t}
                            isArabic={isArabic}
                            required
                          />
                          <PricesNumberField id="monthlyDiscountRate" name="monthlyDiscountRate" label={t('carProfile.form.monthlyDiscountRate')} />
                          <PricesNumberField id="allowedKMPerMonth" name="allowedKMPerMonth" label={t('carProfile.form.allowedKMPerMonth')} required />
                          <PricesNumberField id="extraKMPerMonth" name="extraKMPerMonth" label={t('carProfile.form.extraKMPerMonth')} required />
                          <PricesDateField
                            id="monthlyDiscountStartDate"
                            label={t('carProfile.form.monthlyDiscountFrom')}
                            selected={parseDateSafe(values.monthlyDiscountStartDate)}
                            onChange={date => setFieldValue('monthlyDiscountStartDate', formatDateForBackend(date))}
                            isArabic={isArabic}
                            datePickerLocale={datePickerLocale}
                            placeholderText={selectDateTimePlaceholder}
                          />
                          <PricesDateField
                            id="monthlyDiscountEndDate"
                            label={t('carProfile.form.monthlyDiscountTo')}
                            selected={parseDateSafe(values.monthlyDiscountEndDate)}
                            onChange={date => setFieldValue('monthlyDiscountEndDate', formatDateForBackend(date))}
                            isArabic={isArabic}
                            datePickerLocale={datePickerLocale}
                            placeholderText={selectDateTimePlaceholder}
                          />
                          <PricingRateField
                            id="yearlyRate"
                            name="yearlyRate"
                            activeField="isYearlyActive"
                            label={t('carProfile.form.yearlyRate')}
                            values={values}
                            setFieldValue={setFieldValue}
                            t={t}
                            isArabic={isArabic}
                            required
                          />
                          <PricesNumberField id="yearlyDiscountRate" name="yearlyDiscountRate" label={t('carProfile.form.yearlyDiscountRate')} />
                          <PricesNumberField id="allowedKMPerYear" name="allowedKMPerYear" label={t('carProfile.form.allowedKMPerYear')} required />
                          <PricesNumberField id="extraKMPerYear" name="extraKMPerYear" label={t('carProfile.form.extraKMPerYear')} required />
                          <PricesDateField
                            id="yearlyDiscountStartDate"
                            label={t('carProfile.form.yearlyDiscountFrom')}
                            selected={parseDateSafe(values.yearlyDiscountStartDate)}
                            onChange={date => setFieldValue('yearlyDiscountStartDate', formatDateForBackend(date))}
                            isArabic={isArabic}
                            datePickerLocale={datePickerLocale}
                            placeholderText={selectDateTimePlaceholder}
                          />
                          <PricesDateField
                            id="yearlyDiscountEndDate"
                            label={t('carProfile.form.yearlyDiscountTo')}
                            selected={parseDateSafe(values.yearlyDiscountEndDate)}
                            onChange={date => setFieldValue('yearlyDiscountEndDate', formatDateForBackend(date))}
                            isArabic={isArabic}
                            datePickerLocale={datePickerLocale}
                            placeholderText={selectDateTimePlaceholder}
                          />
                        </div>
                      </div>
                    )}
                    {params?.t === 7 && (
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 items-start">
                          <InsuranceRateField
                            id="insuranceFee"
                            name="insuranceFee"
                            activeField="isStandardInsuranceActive"
                            label={t('carProfile.form.standardInsurancePrice')}
                            suffix={t('common.currency')}
                            values={values}
                            setFieldValue={setFieldValue}
                            isArabic={isArabic}
                          />
                          <InsuranceRateField
                            id="fullInsurancePricePerDay"
                            name="fullInsurancePricePerDay"
                            activeField="isFullInsuranceActive"
                            label={t('carProfile.form.fullInsurancePricePerDay')}
                            suffix={t('common.currency')}
                            values={values}
                            setFieldValue={setFieldValue}
                            isArabic={isArabic}
                          />
                        </div>
                      </div>
                    )}
                    {params?.t === 8 && (
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <p className="text-sm text-gray-500">{t('carProfile.form.extrasHint')}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 items-start">
                          {EXTRA_KEYS.map(key => (
                            <InsuranceRateField
                              key={key}
                              id={`extras-${key}`}
                              name={`extras.${key}.pricePerDay`}
                              activeField={`extras.${key}.active`}
                              label={t(`carProfile.form.${key}PricePerDay`)}
                              suffix={t('common.currency')}
                              values={values}
                              setFieldValue={setFieldValue}
                              isArabic={isArabic}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {params.t === 5 && (!isCompanyRole || hasAdminAccess) && (
                      <div className="mt-1 sm:mt-0 sm:col-span-2 pt-8">
                        <div className="flex flex-wrap gap-4 pb-4 items-center">
                          {carImages.filter(image => image.status === 'pending' || image.status === 'rejected').length > 0 ? (
                            carImages
                              .filter(image => image.status === 'pending' || image.status === 'rejected')
                              .map((image, index) => (
                                <div key={index} className={`${CAR_IMAGE_TILE_CLASS} group`} onClick={() => handleImageClick(image.url, image._id)}>
                                  <img src={image.url} alt={`Car`} className={`cursor-pointer ${CAR_IMAGE_IMG_CLASS}`} />
                                </div>
                              ))
                          ) : (
                            <EmptyState v2 description="No Company Requested Car Images  found" />
                          )}
                        </div>
                      </div>
                    )}

                    {params.t === 6 && (
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        {/* <p className="text-sm font-normal text-gray-500">{t('carProfile.form.vehicleFeaturesHint')}</p> */}
                        <label htmlFor="features.selectAll" className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                          {/* eslint-disable-next-line */}
                          <input
                            id="features.selectAll"
                            type="checkbox"
                            disabled={readOnly}
                            // eslint-disable-next-line
                            checked={VEHICLE_FEATURE_KEYS.every(key => Boolean(values.features?.[key]))}
                            onChange={event => {
                              const next = VEHICLE_FEATURE_KEYS.reduce((acc, key) => ({ ...acc, [key]: event.target.checked }), {})
                              setFieldValue('features', next)
                            }}
                          />
                          {t('carProfile.form.selectAllFeatures')}
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                          {VEHICLE_FEATURE_KEYS.map(featureKey => (
                            <label key={featureKey} htmlFor={`features.${featureKey}`} className="flex items-center gap-2 text-sm border rounded p-3">
                              {/* eslint-disable-next-line */}
                              <input
                                id={`features.${featureKey}`}
                                type="checkbox"
                                disabled={readOnly}
                                checked={Boolean(values.features?.[featureKey])}
                                onChange={event => setFieldValue(`features.${featureKey}`, event.target.checked)}
                              />
                              {t(`carProfile.featureOptions.${featureKey}`)}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {params.t !== 5 && !readOnly && (
                    <div className="pt-2">
                      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
                        <button
                          type="submit"
                          disabled={!isValid || isSubmitting || isReferenceDataLoading}
                          title={isReferenceDataLoading ? t('carProfile.messages.waitForDataToLoad') : undefined}
                          className={classNames(
                            (!isValid || isSubmitting || isReferenceDataLoading) && 'cursor-not-allowed opacity-50',
                            'w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500'
                          )}
                        >
                          {isSubmitting ? (
                            <span className="flex items-start">
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              <span>{t('carProfile.buttons.pleaseWait')}</span>
                            </span>
                          ) : (
                            // <span>{!isEdit ? 'Add' : 'Update'}</span>
                            <span>{!isEdit ? t('carProfile.buttons.add') : t('carProfile.buttons.update')}</span>
                          )}
                        </button>
                        {(!isCompanyRole || hasAdminAccess) && (
                          <div className="w-full sm:w-auto">
                            <button
                              type="button"
                              onClick={deleteCar}
                              className="w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                            >
                              {t('carProfile.buttons.deleteCar')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Form>
              </div>
            </div>
          ) : (
            <div className="w-full h-[500px] flex items-center justify-center">
              <LoadingIcon />
            </div>
          )
        }}
      </Formik>
      <Dialog open={isModalOpen} onClose={closeModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-4xl rounded bg-white p-6">
            <Dialog.Title className="text-lg font-medium mb-4">Requested Company Car</Dialog.Title>

            <ViewCompanyCar imageUrl={selectedImage?.url} imageId={selectedImage?.id} refetch={refetch} onClose={closeModal} carImages={carImages} />
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  )
}

export default CarProfile
